import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CardHeader, chakra, Divider, Flex, Icon, Image, List, ListIcon, ListItem, Text } from '@chakra-ui/react';
import { FormProvider, useForm, ValidateResult } from 'react-hook-form';
import { FaCreditCard, FaFileInvoice } from 'react-icons/fa';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { addDays, startOfDay } from 'date-fns';
import { hasFlag } from '@netiva/classifieds-common';
import {
  DescriptionList,
  DescriptionListItem,
  DescriptionListTerm,
  FormInput,
  FormTextArea,
  Loader,
  SelectableCard,
} from '@netiva/classifieds-ui';

import { useCurrencyFormatter, useDate } from '@/hooks';
import { getIdOrFileName } from '@/lib/utils';
import { InvoicePaymentProvider } from '@/pages/Ad/constants';
import { useAd, useAdAddresses, useAdAttributes, useAdPrice, useAdPublishing } from '@/pages/Ad/hooks';
import { useAppDispatch, useAppSelector } from '@/store';
import { adActions } from '@/store/ad';
import { AddressDisplay, AttributeDisplay } from './components';
import { useAdContactPersons } from '@/pages/Ad/hooks/useContactPersons';

const Section = chakra(Box, {
  baseStyle: {
    marginBottom: 4,
  },
});

const SectionTitle = chakra(Text, {
  baseStyle: {
    fontSize: 'md',
    fontWeight: 'bold',
    marginBottom: 4,
  },
});

const PaymentProviderCardHeader = chakra(CardHeader, {
  baseStyle: {
    p: 1,
    display: 'flex',
    flexDir: 'column',
    alignItems: 'center',
    gap: 2,
    minWidth: '150px',
  },
});

type AdditionalFormValues = {
  remarks: string;
  publishDate: string;
};

export const Review: FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const formatter = useCurrencyFormatter();
  const dateFormatter = useDate();

  const { account } = useAppSelector((state) => state.account);
  const requiresPrepayment = hasFlag(account?.accountStatus, 'RequiresPrepayment');
  const { formData, images, documents, verificationDocuments, paymentProvider, publishRemarks, publishDate } =
    useAppSelector((state) => state.ad);
  const { attributes, isLoading: isLoadingAttributes } = useAdAttributes();
  const {
    activePublication,
    selectedContract,
    selectedProduct,
    selectedExtensionProducts,
    requiresPublication,
    paymentProviders,
  } = useAdPublishing();
  const { isLoading: isLoadingAddresses, /*billingAddress,*/ contactAddress, locationAddress } = useAdAddresses();
  const { isLoading: isLoadingContactPersons, assignedContactPersons: selectedContactPersons } = useAdContactPersons();

  const { uploadedFileData } = useAd();
  const { isFree } = useAdPrice();

  // the window a prolongation buys - mirrors ProductExtensions.GetPublicationEndDate on the server
  const publicationEndDate = activePublication?.endDate;
  const productDuration = selectedProduct?.duration;
  const prolongation = useMemo(() => {
    if (!publicationEndDate || !productDuration) {
      return undefined;
    }
    const startDate = addDays(startOfDay(new Date(publicationEndDate)), 1);
    return { startDate, endDate: addDays(startDate, productDuration) };
  }, [publicationEndDate, productDuration]);

  const setPaymentProvider = useCallback(
    (name: string) => {
      dispatch(adActions.setPaymentProvider(name));
    },
    [dispatch]
  );

  // preselect payment provider
  useEffect(() => {
    if (paymentProviders.length && !paymentProvider) {
      setPaymentProvider(paymentProviders[0]);
    }
  }, [paymentProvider, paymentProviders, setPaymentProvider]);

  // additional form values
  const form = useForm<AdditionalFormValues>({
    values: {
      remarks: publishRemarks,
      publishDate: publishDate,
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    return form.subscribe({
      name: ['publishDate', 'remarks'],
      formState: { values: true },
      callback: ({ values }) => {
        dispatch(adActions.setPublishRemarks(values.remarks || ''));
        dispatch(adActions.setPublishDate(values.publishDate || ''));
      },
    });
  }, [dispatch, form]);

  const validatePublishDate = (value: string): ValidateResult => {
    if (!value) {
      return true;
    }
    const today = startOfDay(new Date());
    const date = new Date(value);
    if (date >= today) {
      return true;
    }
    return t('ad.steps.review.publicationDate.error');
  };

  return (
    <FormProvider {...form}>
      <Section>
        <SectionTitle>{t('ad.steps.review.details')}</SectionTitle>
        <DescriptionList position="relative">
          <Loader isLoading={isLoadingAttributes} />
          {attributes.map((attribute) => {
            const value = formData[attribute.id];
            return value && <AttributeDisplay key={attribute.id} attribute={attribute} value={value} />;
          })}
        </DescriptionList>
      </Section>
      <Divider my={4} orientation="horizontal" />
      {!!images.length && (
        <>
          <Section>
            <SectionTitle>{t('ad.steps.review.images')}</SectionTitle>
            <Flex gap={4} wrap="wrap">
              {images.map((img) => {
                const src = img.url || uploadedFileData.find((fd) => fd.fileName === img.fileName)?.dataUrl || '';
                return <Image key={img.id} src={src} alt={img.fileName} maxWidth={['40%', '120px']} maxHeight="80px" />;
              })}
            </Flex>
          </Section>
          <Divider my={4} orientation="horizontal" />
        </>
      )}
      {!!verificationDocuments.length && (
        <>
          <Section>
            <SectionTitle>{t('ad.steps.review.verificationDocuments')}</SectionTitle>
            <List>
              {verificationDocuments.map((doc) => (
                <ListItem key={getIdOrFileName(doc)}>
                  <ListIcon as={HiOutlineDocumentText} />
                  {doc.fileName}
                </ListItem>
              ))}
            </List>
          </Section>
          <Divider my={4} orientation="horizontal" />
        </>
      )}
      {!!documents.length && (
        <>
          <Section>
            <SectionTitle>{t('ad.steps.review.documents')}</SectionTitle>
            <List>
              {documents.map((doc) => (
                <ListItem key={doc.id}>
                  <ListIcon as={HiOutlineDocumentText} />
                  {doc.fileName}
                </ListItem>
              ))}
            </List>
          </Section>
          <Divider my={4} orientation="horizontal" />
        </>
      )}
      <Section>
        <SectionTitle>{t('ad.steps.review.addresses')}</SectionTitle>
        <DescriptionList position="relative">
          <Loader isLoading={isLoadingAddresses} />
          <AddressDisplay type="contact" address={contactAddress} />
          <AddressDisplay type="location" address={locationAddress} />
          {/* TODO make editable */}
          {/* <AddressDisplay type="billing" address={billingAddress} /> */}
        </DescriptionList>
      </Section>
      {!!selectedContactPersons.length && (
        <Section>
          <SectionTitle>{t('ad.steps.review.contactPersons')}</SectionTitle>
          <DescriptionList position="relative">
            <Loader isLoading={isLoadingContactPersons} />
            {selectedContactPersons.map((cp) => (
              <DescriptionListItem key={cp.id}>
                {cp.firstname} {cp.lastname}, {cp.phone}, {cp.mobile}, {cp.email}
              </DescriptionListItem>
            ))}
          </DescriptionList>
        </Section>
      )}
      {/* platforms without charges still publish through a product, but have nothing worth showing */}
      {!isFree && (
        <>
          <Divider my={4} orientation="horizontal" />
          <Section>
            <SectionTitle>
              {requiresPublication ? t('ad.steps.review.publishing') : t('ad.steps.publishing.currentPublication')}
            </SectionTitle>
            <DescriptionList>
              <DescriptionListTerm>{t('ad.steps.review.product')}</DescriptionListTerm>
              {selectedProduct ? (
                <DescriptionListItem>
                  {selectedProduct.name}
                  {!selectedContract && <> ({formatter.format(selectedProduct.price)})</>}
                </DescriptionListItem>
              ) : (
                activePublication && (
                  <>
                    <DescriptionListItem>{activePublication.contract.product.name}</DescriptionListItem>
                    {activePublication.contract.activeExtensions.map((ext) => (
                      <DescriptionListItem key={ext.id}>{ext.product.name}</DescriptionListItem>
                    ))}
                  </>
                )
              )}
              {selectedContract && (
                <DescriptionListItem>
                  {selectedContract.product.name} ({selectedContract.quantity - selectedContract.available} /{' '}
                  {selectedContract.quantity})
                </DescriptionListItem>
              )}
              {selectedExtensionProducts.map((ep) => (
                <DescriptionListItem key={ep.id}>
                  {ep.name} - ({formatter.format(ep.price)})
                </DescriptionListItem>
              ))}
              {prolongation && (
                <>
                  <DescriptionListTerm>{t('ad.steps.publishing.duration')}</DescriptionListTerm>
                  <DescriptionListItem>
                    {t('ad.steps.publishing.dateFrom')} {dateFormatter.formatDate(prolongation.startDate.toISOString())}{' '}
                    {t('ad.steps.publishing.dateTo')} {dateFormatter.formatDate(prolongation.endDate.toISOString())}
                  </DescriptionListItem>
                </>
              )}
            </DescriptionList>
          </Section>
        </>
      )}
      {(selectedProduct?.allowRemarks || selectedContract?.product.allowRemarks) && (
        <>
          <Divider my={4} orientation="horizontal" />
          <Section>
            <SectionTitle>{t('ad.steps.review.remarks.title')}</SectionTitle>
            <FormTextArea<AdditionalFormValues> label="" name="remarks" isReadOnly={!selectedProduct} />
          </Section>
        </>
      )}
      {(selectedProduct?.allowPublicationDate || selectedContract?.product.allowPublicationDate) && (
        <>
          <Divider my={4} orientation="horizontal" />
          <Section>
            <SectionTitle>{t('ad.steps.review.publicationDate.title')}</SectionTitle>
            <FormInput<AdditionalFormValues>
              type="date"
              label=""
              name="publishDate"
              isReadOnly={!selectedProduct}
              validate={validatePublishDate}
            />
          </Section>
        </>
      )}
      <Divider my={4} orientation="horizontal" />
      {requiresPublication && !!paymentProviders.length && (
        <Section>
          <SectionTitle>
            {t('ad.steps.review.payment.title')}
            <chakra.span color="red" ml={1}>
              *
            </chakra.span>
          </SectionTitle>
          <Flex gap={8}>
            {paymentProviders.map((p) => (
              <SelectableCard
                key={p}
                isSelected={p.toLowerCase() === paymentProvider?.toLowerCase()}
                onClick={() => setPaymentProvider(p)}
              >
                <PaymentProviderCardHeader>
                  <Icon as={FaCreditCard} boxSize={12} />
                  {/* @ts-expect-error dynamic translation */}
                  {t(`ad.steps.review.payment.providers.${p.toLowerCase()}`)}
                </PaymentProviderCardHeader>
              </SelectableCard>
            ))}
            {!requiresPrepayment && (
              <SelectableCard
                isSelected={paymentProvider?.toLowerCase() === InvoicePaymentProvider.toLowerCase()}
                onClick={() => setPaymentProvider(InvoicePaymentProvider)}
              >
                <PaymentProviderCardHeader>
                  <Icon as={FaFileInvoice} boxSize={12} />
                  {t('ad.steps.review.payment.providers.none')}
                </PaymentProviderCardHeader>
              </SelectableCard>
            )}
          </Flex>
        </Section>
      )}
    </FormProvider>
  );
};
